import { useState, useEffect, useMemo } from 'react';
import type { ActivityType } from '../../../types/activityType';
import { activityTypeService } from '../../../services/activityTypeService';
import { operationTypeService } from '../../../services/operationTypeService';
import { productService } from '../../../services/productService';
import { setOperationTypesCache } from '../../../utils/operationTypeUtils';
import { getCreateActivityDraftKey, getEditActivityDraftKey } from '../../../utils/activityDraftStorage';

import type { ActivityFormData } from './types';
import { generateId } from './types';

interface UseActivityGroupDataParams {
    companyId: number;
    groupBid?: number;
    initialDescription?: string;
    initialActivities?: ActivityFormData[];
    /** Existing group's paid-by-client amount, when editing. Undefined in create mode. */
    initialPaidByClientAmount?: number;
    /** Existing group's paid-to-provider amount, when editing. Undefined in create mode. */
    initialPaidToProviderAmount?: number;
    /** Overrides where the in-progress draft is parked in sessionStorage. */
    storageKey?: string;
}

function sumLineTotals(activities: ActivityFormData[], hasParty: (a: ActivityFormData) => boolean): number {
    return activities
        .filter(a => hasParty(a) && a.unitPrice !== undefined && a.amount !== undefined)
        .reduce((sum, a) => sum + a.unitPrice! * a.amount!, 0);
}

function createEmptyActivity(): ActivityFormData {
    return {
        feId: generateId(),
        activityTypeId: undefined,
        productId: undefined,
        amount: undefined,
        stockId: undefined,
        stockFromId: undefined,
        clientId: undefined,
        providerId: undefined,
    };
}

export function useActivityGroupData({
    companyId,
    groupBid,
    initialDescription = '',
    initialActivities,
    initialPaidByClientAmount,
    initialPaidToProviderAmount,
    storageKey: storageKeyOverride,
}: UseActivityGroupDataParams) {
    const storageKey = storageKeyOverride ?? (groupBid
        ? getEditActivityDraftKey(companyId, groupBid)
        : getCreateActivityDraftKey(companyId));

    const loadInitialState = () => {
        // Always check sessionStorage first — covers returning from selection pages,
        // from the availability view, or any other in-app navigation.
        const savedState = sessionStorage.getItem(storageKey);
        if (savedState) {
            try {
                return JSON.parse(savedState);
            } catch (err) {
                console.error('Failed to parse saved state:', err);
            }
        }

        // For edit mode — fall back to fresh BE data when no session state exists
        if (initialActivities) {
            return {
                activities: initialActivities,
                groupDescription: initialDescription,
                paidByClientAmount: initialPaidByClientAmount ?? 0,
                paidToProviderAmount: initialPaidToProviderAmount ?? 0,
            };
        }

        // Create mode default — nothing paid yet, so debt equals the full total until the
        // user deliberately enters what was actually paid.
        return {
            activities: [createEmptyActivity()],
            groupDescription: initialDescription,
            paidByClientAmount: 0,
            paidToProviderAmount: 0,
        };
    };

    const initialState = loadInitialState();

    // Activities state
    const [activities, setActivities] = useState<ActivityFormData[]>(initialState.activities);
    const [groupDescription, setGroupDescription] = useState<string>(initialState.groupDescription);

    // Paid-immediately amounts (drive DEBT_ENTRY charge generation — see be DebtChargeSyncService).
    // Defaults to 0 (nothing paid, full debt) and never auto-tracks the line totals — the user
    // enters what was actually paid.
    const [paidByClientAmount, setPaidByClientAmount] =
        useState<number | undefined>(initialState.paidByClientAmount);
    const [paidToProviderAmount, setPaidToProviderAmount] =
        useState<number | undefined>(initialState.paidToProviderAmount);

    const clientTotal = useMemo(
        () => sumLineTotals(activities, a => a.clientId !== undefined),
        [activities],
    );
    const providerTotal = useMemo(
        () => sumLineTotals(activities, a => a.providerId !== undefined),
        [activities],
    );

    // Reference data
    const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
    const [operationTypesLoaded, setOperationTypesLoaded] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Balance map
    const [balanceMap, setBalanceMap] = useState<Record<number, Record<number, { quantity: number; averageUnitCost?: number }>>>({});

    const balanceFetchKey = useMemo(() => {
        return activities
            .flatMap(a => {
                const pairs: string[] = [];
                if (a.productId && a.stockId) pairs.push(`${a.productId}:${a.stockId}`);
                if (a.productId && a.stockFromId) pairs.push(`${a.productId}:${a.stockFromId}`);
                return pairs;
            })
            .sort()
            .join(',');
    }, [activities]);

    // Fetch balances
    useEffect(() => {
        if (!balanceFetchKey) return;
        const productIds = [...new Set(activities.filter(a => a.productId).map(a => a.productId!))];
        productService.getAvailability(companyId, productIds).then(availList => {
            const map: Record<number, Record<number, { quantity: number; averageUnitCost?: number }>> = {};
            for (const avail of availList) {
                map[avail.productId] = {};
                for (const dist of avail.stockDistribution) {
                    map[avail.productId][dist.stockId] = { quantity: dist.quantity, averageUnitCost: dist.averageUnitCost };
                }
            }
            setBalanceMap(map);
        });
    }, [balanceFetchKey, companyId]);

    // Fetch activity types and operation types
    useEffect(() => {
        const fetchData = async () => {
            try {
                const operationTypesConfig = await operationTypeService.getOperationTypesConfig();
                setOperationTypesCache(operationTypesConfig.operationTypes);
                setOperationTypesLoaded(true);

                const types = await activityTypeService.getAllByCompany(companyId);
                setActivityTypes(types);
            } catch (err) {
                console.error('Failed to load data:', err);
                setLoadError('Не вдалося завантажити типи активностей');
            }
        };
        fetchData();
    }, [companyId]);

    // Persist to sessionStorage
    useEffect(() => {
        const stateToSave = {
            activities,
            groupDescription,
            paidByClientAmount,
            paidToProviderAmount,
        };
        sessionStorage.setItem(storageKey, JSON.stringify(stateToSave));
    }, [activities, groupDescription, paidByClientAmount, paidToProviderAmount, storageKey]);

    // Activity CRUD
    const handleActivityChange = (feId: string, updater: (prev: ActivityFormData) => ActivityFormData) => {
        setActivities(prev => prev.map(activity =>
            activity.feId === feId
                ? updater(activity)
                : activity
        ));
    };

    const handleAddMore = () => {
        setActivities(prev => [...prev, createEmptyActivity()]);
    };

    // Backend ID is dropped so the copy is saved as a new activity, not an update.
    // Margin/cost basis are frozen figures from the source's own backend save — meaningless
    // for a not-yet-saved copy, so they're dropped too rather than shown against the wrong row.
    const copyActivity = (source: ActivityFormData): ActivityFormData => ({
        ...source,
        feId: generateId(),
        bid: undefined,
        margin: undefined,
        unitCostBasis: undefined,
    });

    const handleCopyActivity = (feId: string) => {
        setActivities(prev => {
            const index = prev.findIndex(activity => activity.feId === feId);
            if (index === -1) {
                return prev;
            }
            return [
                ...prev.slice(0, index + 1),
                copyActivity(prev[index]),
                ...prev.slice(index + 1),
            ];
        });
    };

    const handleRemoveActivity = (feId: string) => {
        setActivities(prev => {
            if (prev.length > 1) {
                return prev.filter(activity => activity.feId !== feId);
            }
            return prev;
        });
    };

    const handleRemoveClient = (feId: string) => {
        setActivities(prev => prev.map(activity =>
            activity.feId === feId
                ? { ...activity, clientId: undefined, selectedClient: undefined }
                : activity
        ));
    };

    const handleRemoveProduct = (feId: string) => {
        setActivities(prev => prev.map(activity =>
            activity.feId === feId
                ? { ...activity, productId: undefined, selectedProduct: undefined }
                : activity
        ));
    };

    const handleRemoveProvider = (feId: string) => {
        setActivities(prev => prev.map(activity =>
            activity.feId === feId
                ? { ...activity, providerId: undefined, selectedProvider: undefined }
                : activity
        ));
    };

    const handleRemoveStock = (feId: string) => {
        setActivities(prev => prev.map(activity =>
            activity.feId === feId
                ? { ...activity, stockId: undefined, selectedStock: undefined }
                : activity
        ));
    };

    const handleRemoveStockFrom = (feId: string) => {
        setActivities(prev => prev.map(activity =>
            activity.feId === feId
                ? { ...activity, stockFromId: undefined, selectedStockFrom: undefined }
                : activity
        ));
    };

    const handleReset = () => {
        sessionStorage.removeItem(storageKey);
        setGroupDescription(initialDescription);
        setActivities(initialActivities ?? [createEmptyActivity()]);
        setPaidByClientAmount(initialPaidByClientAmount ?? 0);
        setPaidToProviderAmount(initialPaidToProviderAmount ?? 0);
    };

    return {
        activities,
        setActivities,
        groupDescription,
        setGroupDescription,
        clientTotal,
        providerTotal,
        paidByClientAmount,
        setPaidByClientAmount,
        paidToProviderAmount,
        setPaidToProviderAmount,
        activityTypes,
        operationTypesLoaded,
        loadError,
        balanceMap,
        storageKey,
        handleActivityChange,
        handleAddMore,
        handleCopyActivity,
        handleRemoveActivity,
        handleRemoveClient,
        handleRemoveProduct,
        handleRemoveProvider,
        handleRemoveStock,
        handleRemoveStockFrom,
        handleReset,
    };
}
